import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { investorAccountAPI, fundAPI } from '../services/api';
import { Plus, Search, Briefcase, Trash2, Eye, ChevronUp, ChevronDown, Columns, Upload, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useCurrencyToggle } from '../hooks/useCurrencyToggle';
import AccountBulkImportModal from '../components/investors/AccountBulkImportModal';
import AccountBulkUpdateModal from '../components/investors/AccountBulkUpdateModal';
import TxnNavBulkUploadModal from '../components/investors/TxnNavBulkUploadModal';
import CompletionRing from '../components/investors/CompletionRing';

function fmtINR(v) {
  if (v == null || v === 0) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
}

// Build the section definitions for the configurable list view.
// `fmtAmt(account, bucketKey)` formats a currency-aware total using totals_by_currency.
// `navigate(id)` is invoked when the user clicks the completion ring.
function buildViewSections(fmtAmt, navigate) {
  return {
    account_info: {
      label: 'Account Info',
      columns: [
        { key: 'account_name', label: 'Account Name', render: (a) => a.account_name, bold: true },
        { key: 'completion', label: '% Complete', render: (a) => {
            const c = a.completion || { percent: 0, missing: [] };
            return (
              <CompletionRing
                percent={c.percent}
                missing={c.missing || []}
                onClick={navigate ? (e) => { e.stopPropagation && e.stopPropagation(); navigate(`/investors/accounts/${a.id}`); } : undefined}
                testId={`completion-ring-${a.id}`}
              />
            );
          }, align: 'center', hideOnMobile: false },
        { key: 'type_of_investor', label: 'Type', render: (a) => a.type_of_investor, hideOnMobile: true },
        { key: 'pan', label: 'PAN', render: (a) => a.pan || '-', hideOnMobile: true },
      ],
    },
    contact_address: {
      label: 'Contact and Address',
      columns: [
        { key: 'contact_name', label: 'Contact Name', render: (a) => a.contact_name || '-' },
        { key: 'investor_lead_name', label: 'Investor Lead', render: (a) => a.investor_lead_name || '-', hideOnMobile: true },
        { key: 'distributor_name', label: 'Distributor', render: (a) => a.distributor_name || '-', hideOnMobile: true },
      ],
    },
    nav: {
      label: 'NAV',
      columns: [
        { key: 'latest_nav_value', label: 'Latest NAV', render: (a) => fmtAmt(a, 'nav'), align: 'right' },
        { key: 'latest_nav_date', label: 'NAV Date', render: (a) => a.latest_nav_date || '-', hideOnMobile: true },
      ],
    },
    investment_summary: {
      label: 'Investment Summary',
      columns: [
        { key: 'total_commitment', label: 'Total Commitment', render: (a) => fmtAmt(a, 'commitment'), align: 'right' },
        { key: 'total_called', label: 'Total Called', render: (a) => fmtAmt(a, 'called'), align: 'right' },
        { key: 'total_contribution', label: 'Total Contribution', render: (a) => fmtAmt(a, 'contribution'), align: 'right' },
        { key: 'total_distribution', label: 'Total Distribution', render: (a) => fmtAmt(a, 'distribution'), align: 'right' },
        { key: 'total_nav', label: 'Total NAV', render: (a) => fmtAmt(a, 'nav'), align: 'right' },
      ],
    },
  };
}

// Static section definition used only to enumerate field keys for the picker.
// Render functions are no-ops here — actual rendering uses the in-component VIEW_SECTIONS.
const VIEW_SECTIONS = buildViewSections(() => '-');

// Build flat list of all available fields with section info
const ALL_FIELDS = Object.entries(VIEW_SECTIONS).flatMap(([sectionKey, section]) =>
  section.columns.map(col => ({ ...col, section: sectionKey, sectionLabel: section.label }))
);
const ALL_FIELD_KEYS = ALL_FIELDS.map(f => f.key);

const InvestorAccounts = () => {
  const { canCreate, canDelete } = usePermissions() || {};
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [typeFilter, setTypeFilter] = useState('');
  const [fundFilter, setFundFilter] = useState('');
  const [activeFields, setActiveFields] = useState(() => {
    try {
      const saved = localStorage.getItem('investorAccounts_fields');
      if (!saved) return ALL_FIELD_KEYS;
      const parsed = JSON.parse(saved);
      // Forward-merge: ensure newly-added field keys (e.g. `completion`) are
      // visible by default for users who already have a saved preference set
      // from a previous version.
      const missing = ALL_FIELD_KEYS.filter(k => !parsed.includes(k));
      return missing.length ? [...parsed, ...missing] : parsed;
    } catch { return ALL_FIELD_KEYS; }
  });
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [showTxnNavUpload, setShowTxnNavUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Currency + display-unit toggle (persisted to localStorage)
  const currencyToggle = useCurrencyToggle('investorAccounts_currency');
  const { formatFromBuckets, controlsUI } = currencyToggle;

  // Build runtime VIEW_SECTIONS with currency-aware formatters
  const runtimeViewSections = useMemo(() => buildViewSections(
    (a, bucketKey) => {
      // If backend provided totals_by_currency, prefer it; else fall back to legacy single number.
      if (a && a.totals_by_currency && Object.keys(a.totals_by_currency).length > 0) {
        return formatFromBuckets(a.totals_by_currency, bucketKey);
      }
      // Legacy: use the flat field with default INR source
      const flatKey = bucketKey === 'nav' ? (a.latest_nav_value != null ? 'latest_nav_value' : 'total_nav') : `total_${bucketKey}`;
      const v = a[flatKey];
      if (v == null || v === 0) return '-';
      return currencyToggle.format(v, 'INR');
    },
    navigate,
  ), [formatFromBuckets, currencyToggle, navigate]);

  useEffect(() => { fetchAccounts(); fetchFunds(); }, []);

  useEffect(() => {
    localStorage.setItem('investorAccounts_fields', JSON.stringify(activeFields));
  }, [activeFields]);

  const fetchAccounts = async () => {
    try {
      const res = await investorAccountAPI.getAll();
      setAccounts(res.data);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFunds = async () => {
    try {
      const res = await fundAPI.getAll();
      setFunds(res.data);
    } catch {}
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this investor account and all related data?')) return;
    try {
      await investorAccountAPI.delete(id);
      setAccounts(prev => prev.filter(a => a.id !== id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    } catch { alert('Failed to delete account'); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} investor account(s) and all their related data? This action cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await investorAccountAPI.bulkDelete([...selectedIds]);
      setAccounts(prev => prev.filter(a => !selectedIds.has(a.id)));
      setSelectedIds(new Set());
    } catch {
      alert('Failed to delete selected accounts');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedAccounts.map(a => a.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.add(id)); return next; });
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleField = (fieldKey) => {
    setActiveFields(prev =>
      prev.includes(fieldKey) ? prev.filter(k => k !== fieldKey) : [...prev, fieldKey]
    );
  };

  const toggleSectionFields = (sectionKey) => {
    const sectionFieldKeys = VIEW_SECTIONS[sectionKey].columns.map(c => c.key);
    const allSelected = sectionFieldKeys.every(k => activeFields.includes(k));
    if (allSelected) {
      setActiveFields(prev => prev.filter(k => !sectionFieldKeys.includes(k)));
    } else {
      setActiveFields(prev => [...new Set([...prev, ...sectionFieldKeys])]);
    }
  };

  const filtered = useMemo(() => {
    let list = [...accounts];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter(a =>
        a.account_name?.toLowerCase().includes(t) ||
        a.contact_name?.toLowerCase().includes(t) ||
        a.pan?.toLowerCase().includes(t)
      );
    }
    if (typeFilter) list = list.filter(a => a.type_of_investor === typeFilter);
    if (fundFilter) list = list.filter(a => a.invested_fund_ids && a.invested_fund_ids.includes(fundFilter));
    list.sort((a, b) => {
      // Completion is an object → sort by its .percent.
      if (sortField === 'completion') {
        const aP = (a.completion && a.completion.percent) || 0;
        const bP = (b.completion && b.completion.percent) || 0;
        return sortDir === 'asc' ? aP - bP : bP - aP;
      }
      let aV = a[sortField] || '', bV = b[sortField] || '';
      if (typeof aV === 'number' || typeof bV === 'number') {
        return sortDir === 'asc' ? (aV || 0) - (bV || 0) : (bV || 0) - (aV || 0);
      }
      if (typeof aV === 'string') return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
      return sortDir === 'asc' ? aV - bV : bV - aV;
    });
    return list;
  }, [accounts, searchTerm, typeFilter, fundFilter, sortField, sortDir]);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, typeFilter, fundFilter, pageSize]);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filtered.length / pageSize);
  const paginatedAccounts = pageSize === 'all'
    ? filtered
    : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Build visible columns from active fields (preserving section order)
  // Uses currency-aware render functions from `runtimeViewSections`.
  const visibleColumns = useMemo(() => {
    const allLive = Object.entries(runtimeViewSections).flatMap(([sectionKey, section]) =>
      section.columns.map(col => ({ ...col, section: sectionKey, sectionLabel: section.label }))
    );
    return allLive.filter(f => activeFields.includes(f.key));
  }, [activeFields, runtimeViewSections]);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div data-testid="investor-accounts-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="accounts-title">Investor Accounts</h1>
          <p className="text-gray-600 mt-1">Manage investor accounts, investments, and compliance</p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTxnNavUpload(true)}
              className="inline-flex items-center px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
              data-testid="bulk-upload-txn-nav-btn"
            >
              <FileSpreadsheet size={18} className="mr-2" />
              Upload Txn & FMV
            </button>
            <button
              onClick={() => setShowBulkImport(true)}
              className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              data-testid="bulk-import-accounts-btn"
            >
              <Upload size={18} className="mr-2" />
              Bulk Import
            </button>
            <button
              onClick={() => setShowBulkUpdate(true)}
              className="inline-flex items-center px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors"
              data-testid="bulk-update-accounts-btn"
              title="Update existing accounts in bulk (incremental or replace mode)"
            >
              <RefreshCw size={18} className="mr-2" />
              Bulk Update
            </button>
            <Link to="/investors/accounts/create" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="create-account-btn">
              <Plus size={20} className="mr-2" /> Add Account
            </Link>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search by name, contact, PAN..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="search-accounts" />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" data-testid="type-filter">
            <option value="">All Types</option>
            <option value="Individual">Individual</option>
            <option value="Company">Company</option>
            <option value="LLP">LLP</option>
            <option value="Other">Other</option>
          </select>
          <select value={fundFilter} onChange={e => setFundFilter(e.target.value)} className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" data-testid="fund-filter">
            <option value="">All Funds</option>
            {funds.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <div className="relative">
            <button
              onClick={() => setShowFieldPicker(!showFieldPicker)}
              className="inline-flex items-center px-3 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
              data-testid="field-picker-btn"
              title="Configure visible columns"
            >
              <Columns size={18} />
            </button>
            {showFieldPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-20 w-64 p-3 max-h-80 overflow-y-auto" data-testid="field-picker-dropdown">
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">Visible Columns</p>
                {Object.entries(VIEW_SECTIONS).map(([sectionKey, section]) => {
                  const sectionFieldKeys = section.columns.map(c => c.key);
                  const allSelected = sectionFieldKeys.every(k => activeFields.includes(k));
                  const someSelected = sectionFieldKeys.some(k => activeFields.includes(k));
                  return (
                    <div key={sectionKey} className="mb-2">
                      <label className="flex items-center py-1 cursor-pointer hover:bg-gray-50 rounded px-1 font-medium text-sm text-gray-800">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleSectionFields(sectionKey)}
                          className="mr-2 h-4 w-4 text-blue-600 rounded"
                        />
                        {section.label}
                      </label>
                      <div className="ml-6">
                        {section.columns.map(col => (
                          <label key={col.key} className="flex items-center py-0.5 cursor-pointer hover:bg-gray-50 rounded px-1">
                            <input
                              type="checkbox"
                              checked={activeFields.includes(col.key)}
                              onChange={() => toggleField(col.key)}
                              className="mr-2 h-3.5 w-3.5 text-blue-600 rounded"
                            />
                            <span className="text-sm text-gray-600">{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">{filtered.length} account{filtered.length !== 1 ? 's' : ''}</p>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-400">Show</label>
            <select
              value={pageSize}
              onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="accounts-page-size"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              <option value="all">All</option>
            </select>
          </div>
          {controlsUI}
        </div>
        {canDelete && selectedIds.size > 0 && (
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            data-testid="bulk-delete-accounts-btn"
          >
            <Trash2 size={16} className="mr-2" />
            {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Briefcase size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No investor accounts yet</h3>
          <p className="text-gray-500 mb-6">Add your first investor account to get started</p>
          {canCreate && (
            <Link to="/investors/accounts/create" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus size={20} className="mr-2" /> Add Account
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="accounts-table">
              <thead>
                <tr className="bg-gray-50 border-b text-left text-sm font-medium text-gray-600">
                  {canDelete && (
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={paginatedAccounts.length > 0 && paginatedAccounts.every(a => selectedIds.has(a.id))}
                        ref={el => { if (el) el.indeterminate = paginatedAccounts.some(a => selectedIds.has(a.id)) && !paginatedAccounts.every(a => selectedIds.has(a.id)); }}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                        data-testid="select-all-accounts"
                      />
                    </th>
                  )}
                  {visibleColumns.map(col => (
                    <th
                      key={col.key}
                      className={`px-4 py-3 cursor-pointer select-none ${col.hideOnMobile ? 'hidden lg:table-cell' : ''} ${col.align === 'right' ? 'text-right' : ''}`}
                      onClick={() => toggleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">{col.label} <SortIcon field={col.key} /></span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAccounts.map(acc => (
                  <tr key={acc.id} className={`border-b hover:bg-blue-50/50 cursor-pointer transition-colors ${selectedIds.has(acc.id) ? 'bg-blue-50' : ''}`} onClick={() => navigate(`/investors/accounts/${acc.id}`)} data-testid={`account-row-${acc.id}`}>
                    {canDelete && (
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(acc.id)}
                          onChange={() => toggleSelectOne(acc.id)}
                          className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                          data-testid={`select-account-${acc.id}`}
                        />
                      </td>
                    )}
                    {visibleColumns.map(col => {
                      if (col.key === 'type_of_investor') {
                        return (
                          <td key={col.key} className={`px-4 py-3 text-sm ${col.hideOnMobile ? 'hidden lg:table-cell' : ''}`}>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{acc.type_of_investor}</span>
                          </td>
                        );
                      }
                      return (
                        <td key={col.key} className={`px-4 py-3 text-sm ${col.hideOnMobile ? 'hidden lg:table-cell' : ''} ${col.bold ? 'font-medium text-gray-900' : 'text-gray-600'} ${col.align === 'right' ? 'text-right font-medium' : ''}`}>
                          {col.render ? col.render(acc) : (acc[col.key] || '-')}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={e => { e.stopPropagation(); navigate(`/investors/accounts/${acc.id}`); }} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded">
                          <Eye size={16} />
                        </button>
                        {canDelete && (
                          <button onClick={e => handleDelete(acc.id, e)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {pageSize !== 'all' && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50/50">
              <p className="text-xs text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="accounts-prev-page"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...'
                      ? <span key={`dot-${i}`} className="px-1 text-xs text-gray-400">...</span>
                      : <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`px-3 py-1 text-xs border rounded ${p === currentPage ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-gray-100'}`}
                          data-testid={`accounts-page-${p}`}
                        >
                          {p}
                        </button>
                  )}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="accounts-next-page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close field picker */}
      {showFieldPicker && (
        <div className="fixed inset-0 z-10" onClick={() => setShowFieldPicker(false)} />
      )}

      {/* Bulk Import Modal */}
      <AccountBulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onSuccess={() => { fetchAccounts(); }}
      />

      {/* Bulk Update Modal */}
      <AccountBulkUpdateModal
        open={showBulkUpdate}
        onClose={() => setShowBulkUpdate(false)}
        onSuccess={() => { fetchAccounts(); }}
      />

      {/* Txn & NAV Bulk Upload Modal */}
      <TxnNavBulkUploadModal
        isOpen={showTxnNavUpload}
        onClose={() => setShowTxnNavUpload(false)}
        onSuccess={() => { fetchAccounts(); }}
      />
    </div>
  );
};

export default InvestorAccounts;
